import { Card, CardContent, CircularProgress, Unstable_Grid2 as Grid } from '@mui/material';

import { AudienceBasicFragment, GeneralSurveyFragment } from '@occc/graphql';
import { Divider, InfoField, LinearProgress, LinkChip, Typography } from '@occc/ui-design-system';
import { LoadedData } from '@occc-app/routes/Wallet/types';
import { routeCreator } from '@occc-app/routes/Audience/utils';
import { DateTimeFormat } from '@occc-app/lib/date';
import { readableDate, readableTimezone } from '@occc-app/lib/format';
import { getSurveyAwardDetails } from '@occc-app/routes/Wallet/WalletDetailsPage/utils';

import { SurveyStatisticsBarChartItem, SurveyStatisticsPieChartItem } from './types';
import { AllAnswersChartSection } from './AllAnswersChartSection';

import { titleByQuestionTypeName } from '../constants';

export type SurveyDetailsProps = {
  surveyData: LoadedData<GeneralSurveyFragment>;
  audienceData: LoadedData<AudienceBasicFragment>;
  allAnswersChartSectionProps: SurveyStatisticsPieChartItem | SurveyStatisticsBarChartItem | null;
};

export const SurveyDetails = ({
  surveyData,
  audienceData,
  allAnswersChartSectionProps,
}: SurveyDetailsProps) => {
  if (surveyData.error) {
    return <Typography color='error'>Something went wrong. Please try again.</Typography>;
  }

  if (surveyData.loading && !surveyData.data) {
    return <CircularProgress />;
  }

  if (!surveyData.data) {
    return <Typography color='error'>No data. Something went wrong. Please try again.</Typography>;
  }

  const survey = surveyData.data;
  const surveyAward = getSurveyAwardDetails(survey);
  const question = survey.questions[0];

  return (
    <Card sx={{ mt: 2 }}>
      {surveyData.loading && <LinearProgress />}
      <CardContent>
        <Grid container spacing={2}>
          <Grid sm={allAnswersChartSectionProps?.items.length ? 6 : 12} xs={12}>
            <Typography variant='subtitle1' mb={3}>
              Survey Details
            </Typography>
            <Grid container spacing={2} mb={0}>
              <Grid xs={12} md={4}>
                <InfoField label='Survey Question' value={question.questionText} />
              </Grid>
              <Grid xs={12} md={4}>
                <InfoField
                  label='Survey Type'
                  value={question ? titleByQuestionTypeName[question.__typename] : null}
                />
              </Grid>
              <Grid xs={12} md={4}>
                <InfoField label='Incentive' value={surveyAward ?? 'Not configured'} />
              </Grid>
              <Grid xs={12} md={4}>
                <InfoField
                  label='Survey Audience'
                  value={
                    audienceData.data ? (
                      <LinkChip
                        label={audienceData.data.name}
                        href={routeCreator.details(survey.audienceUuid)}
                      ></LinkChip>
                    ) : (
                      <CircularProgress size={28} />
                    )
                  }
                />
              </Grid>
            </Grid>
            <Divider />
            <Typography variant='subtitle1' my={3}>
              Schedule
            </Typography>
            <Grid container spacing={2}>
              <Grid xs={12} md={4}>
                <InfoField
                  label='From'
                  value={readableDate(survey.configuration.startDate, DateTimeFormat.DateTime12h)}
                />
              </Grid>
              <Grid xs={12} md={4}>
                <InfoField
                  label='To'
                  value={
                    readableDate(survey.configuration.endDate, DateTimeFormat.DateTime12h) ||
                    'Not configured'
                  }
                />
              </Grid>
              <Grid xs={12} md={4}>
                <InfoField label='Timezone' value={readableTimezone()} />
              </Grid>
            </Grid>
          </Grid>

          {!!allAnswersChartSectionProps?.items.length && (
            <AllAnswersChartSection {...allAnswersChartSectionProps} />
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};
